import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, Users, UserPlus, CalendarDays, Activity, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fold } from "@/lib/format";
import { t, locale } from "@/i18n";

const dt = (iso) => (iso ? new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center justify-between"><p className="eyebrow text-[10px] sm:text-xs">{label}</p><Icon className="h-4 w-4 text-muted-foreground" /></div>
      <p className="num mt-2 text-2xl font-bold" data-testid={`${testId}-value`}>{value}</p>
    </div>
  );
}

function Tag({ u }) {
  if (u.role === "admin") return <span className="rounded bg-[hsl(var(--ai)/0.15)] px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--ai))]">admin</span>;
  if (u.is_demo) return <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{t("adminUsers.demo")}</span>;
  return null;
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => { if (user?.role === "admin") api.get("/admin/users").then(({ data }) => setData(data)).catch(() => setError(true)); }, [user]);
  const rows = useMemo(() => (data?.users || []).filter((u) => fold(`${u.name} ${u.email} ${u.restaurant_name}`).includes(fold(q))), [data, q]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (error) return <p className="py-16 text-center text-sm text-muted-foreground" data-testid="admin-error">{t("adminUsers.impossible_de_charger_les_utilisateurs_ree")}</p>;
  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="admin-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const s = data.stats;

  return (
    <>
      <PageHeader eyebrow={t("common.administration")} title={t("adminUsers.utilisateurs_inscrits")} description={t("adminUsers.comptes_crees_sur_mymenuprofit_les_statist")} />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Stat icon={Users} label={t("adminUsers.inscrits")} value={s.total} testId="admin-stat-total" />
        <Stat icon={UserPlus} label={t("adminUsers.7_jours")} value={s.last_7_days} testId="admin-stat-7d" />
        <Stat icon={CalendarDays} label={t("adminUsers.30_jours")} value={s.last_30_days} testId="admin-stat-30d" />
        <Stat icon={Activity} label={t("adminUsers.actifs")} value={s.activated} testId="admin-stat-activated" />
      </div>
      <div className="relative mb-4 md:max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder={t("adminUsers.rechercher_un_utilisateur")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search-input" />
      </div>
      <div className="space-y-2 md:hidden" data-testid="admin-users-mobile-list">
        {rows.map((u) => (
          <div key={u.id} className="rounded-xl border bg-card p-3.5" data-testid={`admin-user-card-${u.id}`}>
            <div className="flex items-center gap-2"><p className="truncate font-medium">{u.name}</p><Tag u={u} /></div>
            <p className="truncate text-xs text-muted-foreground">{u.email} · {u.restaurant_name}{u.signup_city ? ` · ${u.signup_city}` : ""}</p>
            <p className="mt-1 text-xs">{t("adminUsers.inscrit_le")} {dt(u.created_at)} · {u.recipes_count} {t("adminUsers.recette_s")} {t("common.n_invoices_paren", { v0: u.invoices_count })}</p>
          </div>
        ))}
      </div>
      <div className="hidden overflow-x-auto rounded-xl border bg-card md:block">
        <Table data-testid="admin-users-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("adminUsers.utilisateur")}</TableHead>
              <TableHead>{t("adminUsers.restaurant")}</TableHead>
              <TableHead>{t("adminUsers.ville")}</TableHead>
              <TableHead>{t("adminUsers.date_d_inscription")}</TableHead>
              <TableHead className="text-right">{t("common.recettes")}</TableHead>
              <TableHead className="text-right">{t("common.factures")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell><div className="flex items-center gap-2"><p className="font-medium">{u.name}</p><Tag u={u} /></div><p className="text-xs text-muted-foreground">{u.email}</p></TableCell>
                <TableCell>{u.restaurant_name}</TableCell>
                <TableCell className="text-muted-foreground" data-testid={`admin-user-city-${u.id}`}>{u.signup_city || "—"}</TableCell>
                <TableCell className="text-muted-foreground" data-testid={`admin-user-date-${u.id}`}>{dt(u.created_at)}</TableCell>
                <TableCell className="num text-right">{u.recipes_count}</TableCell>
                <TableCell className="num text-right">{u.invoices_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">{t("adminUsers.aucun_utilisateur_trouve")}</p>}
    </>
  );
}
